import axiosClient from '../axiosClient';
import type { TableDefinitionResponse } from '@/api/contracts/tableDefinitionsCore';
import type { UpdateColumnDefinitionRequest } from '@/api/endpoints/tableColumns';

export type TableEntry = TableDefinitionResponse;

/** Fjerner interne DB-ID-er fra GET v2/table-respons før de lagres i Pinia. */
function stripInternalTablePayload (raw: unknown): TableDefinitionResponse {
  if (raw == null || typeof raw !== 'object') {
    throw new Error('Uventet tabellrespons');
  }
  const data = raw as TableDefinitionResponse;
  const tableKey = String(data.tableKey || data.publicId || '').trim() || null;
  return { ...data, tableKey, columns: data.columns ?? [] };
}
export type SaveTableColumnDefinitionRequest = UpdateColumnDefinitionRequest;
export interface SaveTableColumnDefinitionResponse {
  numberFormatChanged?: boolean;
}

export interface ToggleColumnRequest {
  tableKey: string;
  columnName: string;
  newValue: boolean;
}

export interface CreateTableDefinitionRequest {
  dbSchemaName: string;
  dbTableName: string;
  name?: string;
  alias?: string;
  description?: string;
}

/** Kolonnedefinisjon for create-table. Må matche backend DdlColumnDefinition (camelCase). columnName brukes som DB-kolonnenavn når satt (f.eks. fra fil-parsing). */
export interface CreateTableRequest {
  dbSchemaName: string;
  dbTableName: string;
  name?: string;
  alias?: string;
  description?: string;
  columnTypes?: Array<{
    columnName?: string | null;
    columnLabel: string;
    primaryFieldType: string;
    maxLength?: number | null;
    precision?: number | null;
    scale?: number | null;
    defaultValue?: string | null;
    nullHandling?: string;
    includeInputFormNew?: boolean;
    isReadOnly?: boolean;
    [key: string]: unknown;
  }>;
  menuItemKey?: string | null;
}

export interface CopyTableRequest {
  fromSchemaName: string;
  fromTableName: string;
  toSchemaName: string;
  toTableName: string;
  copyData: boolean;
  menuItemKey?: string | null;
  name?: string;
  alias?: string;
  description?: string;
}

export interface UpdateTableMetadataRequest {
  name?: string;
  alias?: string;
  description?: string;
}

export interface CreateTableResult {
  publicId: string;
  /** Normaliserte identifikatorer slik de ble brukt ved opprettelse (API-svar). */
  schemaName?: string | null;
  tableName?: string | null;
}

export interface LinkTableRequest {
  menuItemKey: string;
  tableKey: string;
}

export interface UnlinkTableRequest {
  tableKey: string;
}

export interface DeleteTableRequest {
  tableKey: string;
}

export interface TableDeleteDryRunImportItem {
  importDefinitionKey: string;
  name: string | null;
}

export interface TableDeleteDryRunValidationRuleItem {
  validationRuleId: number;
  columnName: string;
  ruleType: string;
}

export interface ViewToDropItem {
  schemaName: string;
  viewName: string;
}

export interface TableDeleteDryRunDataViewItem {
  viewId: string;
  name: string;
  modelId: string;
}

export interface TableDeleteDryRunDataModelItem {
  modelId: string;
  name: string;
  isSystemGenerated: boolean;
}

export interface TableDeleteDryRunResponse {
  tableSchemaName: string;
  tableName: string;
  tableDisplayName: string | null;
  importDefinitions: TableDeleteDryRunImportItem[];
  validationRules: TableDeleteDryRunValidationRuleItem[];
  viewsToDrop: ViewToDropItem[];
  dataViews: TableDeleteDryRunDataViewItem[];
  dataModels: TableDeleteDryRunDataModelItem[];
  affectedDataViews: TableDeleteDryRunDataViewItem[];
  affectedDataModels: TableDeleteDryRunDataModelItem[];
  isMinimalImpact: boolean;
}

export interface MenuIntegrityValidationIssue {
  code: string;
  severity: string;
  publicId?: string | null;
  objectType?: string | null;
  dependencyType?: string | null;
  message: string;
}

export interface MenuIntegrityValidationResponse {
  allGreen: boolean;
  checkedCount: number;
  issueCount: number;
  warningCount: number;
  issues: MenuIntegrityValidationIssue[];
}

// GET endpoints
export async function fetchTableByKey (tableKey: string): Promise<TableEntry | undefined> {
  const normalized = String(tableKey ?? '').trim();
  if (!normalized) {
    return undefined;
  }

  const response = await axiosClient.get('v2/table', {
    params: { tableKey: normalized },
  });
  return stripInternalTablePayload(response.data);
}

// Bug 2323: per-kolonne relasjons-oppslag-config for en tabell (uavhengig av data-view). Brukes av
// «Ny rad» sub-create slik at relasjonskolonner i en koblet barn-tabell rendres som dropdown/oppslag.
export interface TableRelationLookupColumn {
  columnKey: string;
  relationLookupEnabled: boolean;
  relationTargetTableKey: string | null;
}

export interface TableRelationLookupConfigResponse {
  columns: TableRelationLookupColumn[];
}

// US 2665 (Task 2670): gyldige mål for en løpenummer-referanse — BE eier filtreringen
// (tabeller med løpenummer i hode-modus); FE presenterer listen i mål-velgeren.
//
// Bug 3632: schemaName is what tells two target tables apart when they carry the same name in
// different schemas. Backend owns the name and the frontend renders it verbatim.
export interface SequenceReferenceTargetOption {
  tableKey: string;
  schemaName: string;
  tableName: string;
  displayName: string | null;
  columnKey: string;
  columnLabel: string | null;
}

export interface SequenceReferenceTargetsResponse {
  targets: SequenceReferenceTargetOption[];
}

export async function getSequenceReferenceTargets (): Promise<SequenceReferenceTargetOption[]> {
  const response = await axiosClient.get<SequenceReferenceTargetsResponse>('v2/table/sequence-reference-targets');
  return response.data.targets;
}

export async function fetchTableRelationLookupConfig (
  tableKey: string
): Promise<TableRelationLookupConfigResponse | null> {
  const normalized = String(tableKey ?? '').trim();
  if (!normalized) {
    return null;
  }

  const response = await axiosClient.get<TableRelationLookupConfigResponse>('v2/table/relation-lookup-config', {
    params: { tableKey: normalized },
  });
  return response.data;
}

export async function getTableDeleteDryRun (
  tableKey: string,
  signal?: AbortSignal
): Promise<TableDeleteDryRunResponse | null> {
  try {
    const normalized = String(tableKey || '').trim();
    if (!normalized) {
      return null;
    }
    const response = await axiosClient.get<TableDeleteDryRunResponse>('v2/database/system/table/delete-dry-run', {
      params: { tableKey: normalized },
      signal,
    });
    return response.data;
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axErr = err as { response?: { status?: number } };
      if (axErr.response?.status === 404) return null;
    }
    throw err;
  }
}

// POST endpoints
export async function saveTableColumnDefinition (
  tableKey: string,
  request: SaveTableColumnDefinitionRequest
): Promise<SaveTableColumnDefinitionResponse> {
  const response = await axiosClient.post<SaveTableColumnDefinitionResponse>('v2/table/column/definition', request, {
    params: { tableKey }
  });
  return response.data ?? {};
}

function mapCreateTableResult (data: { publicId?: string | null; schemaName?: string | null; tableName?: string | null } | undefined): CreateTableResult {
  return {
    publicId: String(data?.publicId || '').trim(),
    schemaName: data?.schemaName ?? null,
    tableName: data?.tableName ?? null,
  };
}

export async function createTableDefinition (
  request: CreateTableDefinitionRequest
): Promise<CreateTableResult> {
  const response = await axiosClient.post('v2/database/system/create-table-definition', request);
  return mapCreateTableResult(response.data);
}

export async function addTable (
  request: CreateTableDefinitionRequest
): Promise<CreateTableResult> {
  const response = await axiosClient.post('v2/database/system/add-table', request);
  return mapCreateTableResult(response.data);
}

export async function createTable (
  request: CreateTableRequest
): Promise<CreateTableResult> {
  const response = await axiosClient.post('v2/database/system/create-table', request);
  return mapCreateTableResult(response.data);
}

export async function copyTable (
  request: CopyTableRequest
): Promise<CreateTableResult> {
  const response = await axiosClient.post('v2/database/system/copy-table', request);
  return mapCreateTableResult(response.data);
}

export async function validateMenuIntegrity (): Promise<MenuIntegrityValidationResponse> {
  const response = await axiosClient.post<MenuIntegrityValidationResponse>(
    'v2/database/system/validate-menu-integrity'
  );
  return response.data;
}

export async function updateTableMetadata (
  tableKey: string,
  request: UpdateTableMetadataRequest
): Promise<void> {
  await axiosClient.put(`v2/table/${tableKey}/metadata`, request);
}

export interface UpdateTableAccessRolesRequest {
  readRoleIds: string[];
  writeRoleIds: string[];
}

export async function updateTableAccessRoles (
  tableKey: string,
  request: UpdateTableAccessRolesRequest
): Promise<void> {
  await axiosClient.put(`v2/table/${tableKey}/access-roles`, request);
}

export interface DataViewSummaryItem {
  viewId: string;
  name: string;
  // WI 2082: visningens offentlige nøkkel — den FE navigerer med. Utelates på
  // wire for historiske visninger uten publicId (BE serialiserer ikke null).
  publicId?: string | null;
}

export async function getDataViewsUsingTableDefinition (tableKey: string): Promise<DataViewSummaryItem[]> {
  const response = await axiosClient.get<DataViewSummaryItem[]>(`v2/table/${tableKey}/data-views`);
  return response.data ?? [];
}

export interface SyncColumnDisplayFromTableDefinitionRequest {
  columnName: string;
  numberFormatShowThousandsSeparator?: boolean | null;
  numberFormatDecimalPrecision?: number | null;
  dataViewIds: string[];
}

export interface ActivateColumnInDataViewsRequest {
  columnName: string;
  dataViewIds: string[];
}

export async function syncColumnDisplayFromTableDefinition (
  tableKey: string,
  request: SyncColumnDisplayFromTableDefinitionRequest
): Promise<void> {
  await axiosClient.post(`v2/table/${tableKey}/sync-column-display`, request);
}

export async function activateColumnInDataViews (
  tableKey: string,
  request: ActivateColumnInDataViewsRequest
): Promise<void> {
  await axiosClient.post(`v2/table/${tableKey}/activate-column-in-data-views`, request);
}

// PUT endpoints
export async function toggleColumnReadOnly (
  request: ToggleColumnRequest
): Promise<void> {
  await axiosClient.put('v2/table/column/readonly', null, {
    params: request
  });
}

// US 2937: per-kolonne fritekst-søkbarhet (skrive-sti).
export async function toggleColumnSearchable (
  request: ToggleColumnRequest
): Promise<void> {
  await axiosClient.put('v2/table/column/searchable', null, {
    params: request
  });
}

export async function toggleColumnIncludeInputFormNew (
  request: ToggleColumnRequest
): Promise<void> {
  await axiosClient.put('v2/table/column/include-input-form-new', null, {
    params: request
  });
}

export async function linkTable (
  request: LinkTableRequest
): Promise<void> {
  await axiosClient.put('v2/database/system/table/link-by-key', null, {
    params: {
      menuItemKey: String(request.menuItemKey || '').trim(),
      tableKey: String(request.tableKey || '').trim(),
    },
  });
}

export async function unlinkTable (
  request: UnlinkTableRequest
): Promise<void> {
  await axiosClient.put('v2/database/system/table/unlink-by-key', null, {
    params: { tableKey: String(request.tableKey || '').trim() },
  });
}

// DELETE endpoints
export async function deleteTable (
  request: DeleteTableRequest
): Promise<void> {
  await axiosClient.delete('v2/database/system/table', {
    params: request
  });
}

/** Datamodell som peker på tabellen (del av tilkoblings-sammenstillingen). */
export interface TableConnectionDataModel {
  modelId: string;
  name: string;
}

/** Datavisning på en av tabellens datamodeller. */
export interface TableConnectionDataView {
  viewId: string;
  modelId: string;
  name: string;
}

/** Importdefinisjon for tabellen. */
export interface TableConnectionImportDefinition {
  importDefinitionKey: string;
  name: string | null;
  active: boolean;
}

/**
 * Aggregert tilkoblings-sammenstilling for et tabelloppsett. Backend samler de tre kildene
 * (datamodeller, datavisninger, importdefinisjoner) frontend tidligere sammenstilte selv, pluss
 * tabellens menypunkt.
 */
export interface TableConnectionsResponse {
  menuItemKey: string | null;
  dataModels: TableConnectionDataModel[];
  dataViews: TableConnectionDataView[];
  importDefinitions: TableConnectionImportDefinition[];
}

export async function getTableConnections (tableKey: string): Promise<TableConnectionsResponse> {
  const response = await axiosClient.get<TableConnectionsResponse>(
    `v2/table/${encodeURIComponent(tableKey)}/connections`
  );
  return response.data;
}
